const FactoryLeadTimeIntervalOptions = {
    DAYS: 0,
    WEEKS: 1,
    MONTHS: 2,
};

const INTERVAL_VALUES = Object.values(FactoryLeadTimeIntervalOptions);

type MessageResource =
    | "QuantityInStock"
    | "DropShipQuantity"
    | "ValueAddQuantity"
    | "OnOrderQuantity"
    | "FactoryStockQuantity"
    | "FactoryLeadTimeDay"
    | "FactoryLeadTimeDays"
    | "FactoryLeadTimeWeek"
    | "FactoryLeadTimeWeeks"
    | "FactoryLeadTimeMonth"
    | "FactoryLeadTimeMonths";

export default function usePartStock() {
    const { t } = useI18n();

    function getMessage(resource: MessageResource, value?: number) {
        return value ? t("Search." + resource, [formatNumber(value)]) : "";
    }

    function getStockMessages(partResult: Partial<PartResult>) {
        return [
            getMessage("DropShipQuantity", partResult.DropShipQuantity),
            getMessage("ValueAddQuantity", partResult.ValueAddQuantity),
            getMessage("OnOrderQuantity", partResult.OnOrderQuantity),
            partResult.DisplayFactoryStock ? getMessage("FactoryStockQuantity", partResult.FactoryStockQuantity) : "",
        ].filter((msg) => msg);
    }

    function getLeadTimeMessage(partResult: Partial<PartResult>) {
        if (
            !partResult.DisplayFactoryLeadTime ||
            !partResult.FactoryLeadTime ||
            partResult.FactoryLeadTimeInterval == null ||
            !INTERVAL_VALUES.includes(partResult.FactoryLeadTimeInterval)
        ) {
            return "";
        }

        let interval = "";
        const time = partResult.FactoryLeadTime;
        switch (partResult.FactoryLeadTimeInterval) {
            case FactoryLeadTimeIntervalOptions.DAYS:
                interval = getMessage(time == 1 ? "FactoryLeadTimeDay" : "FactoryLeadTimeDays", time);
                break;
            case FactoryLeadTimeIntervalOptions.WEEKS:
                interval = getMessage(time == 1 ? "FactoryLeadTimeWeek" : "FactoryLeadTimeWeeks", time);
                break;
            case FactoryLeadTimeIntervalOptions.MONTHS:
                interval = getMessage(time == 1 ? "FactoryLeadTimeMonth" : "FactoryLeadTimeMonths", time);
                break;
        }

        return t("Search.FactoryLeadTime", [interval]);
    }

    function partHasAnyStock(partResult: Partial<PartResult>) {
        return (
            partResult.QuantityOnHand ||
            partResult.DropShipQuantity ||
            partResult.ValueAddQuantity ||
            partResult.OnOrderQuantity ||
            partResult.FactoryStockQuantity ||
            partResult.FactoryLeadTime
        );
    }

    function getStockLabel(partResult: Partial<PartResult>) {
        if (partResult.QuantityOnHand) return getMessage("QuantityInStock", partResult.QuantityOnHand);
        if (partResult.DropShipQuantity) return getMessage("DropShipQuantity", partResult.DropShipQuantity);
        if (partResult.ValueAddQuantity) return getMessage("ValueAddQuantity", partResult.ValueAddQuantity);
        if (partResult.OnOrderQuantity) return getMessage("OnOrderQuantity", partResult.OnOrderQuantity);
        if (partResult.DisplayFactoryStock && partResult.FactoryStockQuantity)
            return getMessage("FactoryStockQuantity", partResult.FactoryStockQuantity);

        const leadTimeMessage = getLeadTimeMessage(partResult);
        return leadTimeMessage ? leadTimeMessage : t("Global.OutOfStock");
    }

    return { getStockMessages, getLeadTimeMessage, partHasAnyStock, getStockLabel };
}
